import React from 'react';
import { useLoaderData, useParams } from 'react-router-dom';

import { UserGroupIcon } from '@heroicons/react/24/outline';
import { Await } from '@oysterjs/ui/async';
import { PageOutlet } from '@oysterjs/ui/navigation';
import { PageTitle } from '@oysterjs/ui/page';
import { SegmentedNavigation } from '@oysterjs/ui/segmented-control';
import { Spinner } from '@oysterjs/ui/spinner';

import { ClientFieldsFragment, useGetClientQuery } from '~/api/graphql';
import { ClientActionsMenu } from '~/components/clients/client-actions';
import { ClientTabEmptyState } from '~/components/clients/client-panels';
import { ClientLinesBadges } from '~/components/clients/clients-columns';

const CLIENT_TABS = [
  { path: 'overview', title: 'Overview' },
  // No tab carries a count. The lists are paginated, so a number here would be
  // one page rather than a total, and the honest total costs a scan of every
  // matching row.
  { path: 'workspaces', title: 'Workspaces' },
  { path: 'documents', title: 'Documents' },
  { path: 'uploads', title: 'Uploads' },
  { path: 'notes', title: 'Notes' }
];

/**
 * The header reads the client through a cache-first query rather than straight
 * from the loader, so an edit made from the Overview lands here too.
 */
const ClientPageLayout = ({ clientId }: { clientId: string }) => {
  const { data } = useGetClientQuery({ variables: { id: clientId }, fetchPolicy: 'cache-first' });
  const client = data?.client;
  if (!client) {
    return null;
  }

  return (
    <PageTitle
      eyebrow="Clients"
      eyebrowHref="/app/clients"
      title={client.name}
      badge={
        client.isCommercial || client.isPersonal ? (
          <ClientLinesBadges isCommercial={client.isCommercial} isPersonal={client.isPersonal} />
        ) : undefined
      }
      menu={<ClientActionsMenu client={client} />}
    >
      <SegmentedNavigation routes={CLIENT_TABS} aria-label="Client sections" />
      <div className="mt-6">
        <PageOutlet />
      </div>
    </PageTitle>
  );
};

/** Layout for one client: identity header, section switcher, and the active section. */
export const ClientPage = () => {
  const { id = '' } = useParams();
  const loader = useLoaderData() as { data: Promise<{ client: ClientFieldsFragment | null }> };

  return (
    <React.Suspense
      fallback={
        <div className="py-12 text-center">
          <Spinner size="size-8" color="neutral" />
        </div>
      }
    >
      <Await resolve={loader?.data}>
        {({ client }) =>
          client ? (
            <ClientPageLayout clientId={id} />
          ) : (
            <PageTitle eyebrow="Clients" eyebrowHref="/app/clients" title="Client not found">
              <ClientTabEmptyState
                icon={UserGroupIcon}
                title="Client not found"
                description="It may have been deleted, or belong to another agency."
              />
            </PageTitle>
          )
        }
      </Await>
    </React.Suspense>
  );
};
